import type { CustomGroupSensor } from '@metron/shared';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Loader2, X } from 'lucide-react';

export interface SensorListProps {
  sensors: CustomGroupSensor[];
  onRemoveSubmeter: (sensorId: number) => void;
  onRemoveMaster: (sensorId: number) => void;
  isLoading: boolean;
  hasEditPermission: boolean;
}

export function SensorList({
  sensors,
  onRemoveSubmeter,
  onRemoveMaster,
  isLoading,
  hasEditPermission,
}: SensorListProps) {
  const submeters = sensors.filter((s) => s.role === 'submeter');
  const masters = sensors.filter((s) => s.role === 'master');

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 py-4'>
        <Loader2 className='h-4 w-4 animate-spin' />
        <span className='text-sm text-muted-foreground'>Loading sensors...</span>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Submeters Section */}
      <div className='space-y-2'>
        <Label className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
          Submeters ({submeters.length})
        </Label>
        {submeters.length === 0
          ? <p className='text-sm text-muted-foreground'>No submeters assigned</p>
          : (
            <div className='space-y-1'>
              {submeters.map((sensor) => (
                <div
                  key={sensor.sensorId}
                  className='flex items-center justify-between rounded-md border px-3 py-2'
                >
                  <span className='text-sm font-mono'>{sensor.deviceId}</span>
                  {hasEditPermission && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-6 w-6 p-0 text-muted-foreground hover:text-destructive'
                      onClick={() => onRemoveSubmeter(sensor.sensorId)}
                      title='Remove submeter'
                    >
                      <X className='h-3 w-3' />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Master Meters Section */}
      <div className='space-y-2'>
        <Label className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
          Master Meters ({masters.length})
        </Label>
        {masters.length === 0
          ? <p className='text-sm text-muted-foreground'>No master meters assigned</p>
          : (
            <div className='space-y-1'>
              {masters.map((sensor) => (
                <div
                  key={sensor.sensorId}
                  className='flex items-center justify-between rounded-md border px-3 py-2'
                >
                  <span className='text-sm font-mono'>{sensor.deviceId}</span>
                  {hasEditPermission && (
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-6 w-6 p-0 text-muted-foreground hover:text-destructive'
                      onClick={() => onRemoveMaster(sensor.sensorId)}
                      title='Remove master meter'
                    >
                      <X className='h-3 w-3' />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
